"use client"

import { CheckCircle2, ClipboardCheck } from "lucide-react"

import { Button } from "@/common/components/ui/button"
import { cn } from "@/common/lib/utils"
import { BAUSTEIN_BY_ID } from "@/modules/lernpfad/data/bausteine"
import { addDays, evidenceIndex, parseIsoDate, REQUIRED_DRIVERS, resolvePolicy } from "@/modules/lernpfad/engine"
import type { EmployeeProfile, RequiredDriver } from "@/modules/lernpfad/engine"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { SectionHeading } from "../ui-bits"

/**
 * The manager's half of Kirkpatrick Level 3.
 *
 * New World Kirkpatrick's finding is that transfer does not survive on its own:
 * it survives where somebody monitors, reinforces, encourages and rewards it —
 * the "required drivers". Those are a manager's job, not L&D's, which is why
 * this queue lives in the Führung view and why the drivers are checkboxes
 * rather than prose.
 *
 * A row appears only inside the confirmation window (14–30 days after
 * completion, both ends policy-set). Outside it, asking is either premature or
 * archaeology.
 */

interface QueueRow {
    person: EmployeeProfile
    bausteinId: string
    title: string
    completedOn: string
    dueBy: string
    confirmed: boolean
    drivers: RequiredDriver[]
}

export default function TransferQueue({
    state,
    team,
}: {
    state: LernpfadDemoState
    team: EmployeeProfile[]
}) {
    const { t, localizer, asOf, policy, confirmTransfer, confirmedTransfers } = state
    const resolved = resolvePolicy(policy)
    const { from, to } = resolved.evidence.behaviourWindowDays
    const asOfMs = parseIsoDate(asOf)

    const rows: QueueRow[] = []
    for (const person of team) {
        const evidence = evidenceIndex(person.evidence)
        for (const completion of person.completed) {
            const opens = parseIsoDate(addDays(completion.completedOn, from))
            const closes = parseIsoDate(addDays(completion.completedOn, to))
            if (asOfMs < opens || asOfMs > closes) continue

            const record = evidence.get(completion.bausteinId)
            const key = `${person.id}:${completion.bausteinId}`
            const local = confirmedTransfers[key]
            if (record?.behaviour && !local) continue // already answered in the record

            const baustein = BAUSTEIN_BY_ID.get(completion.bausteinId)
            if (!baustein) continue
            rows.push({
                person,
                bausteinId: completion.bausteinId,
                title: localizer.baustein(baustein).title,
                completedOn: completion.completedOn,
                dueBy: addDays(completion.completedOn, to),
                confirmed: local !== undefined,
                drivers: local ?? [],
            })
        }
    }

    rows.sort((a, b) => parseIsoDate(a.dueBy) - parseIsoDate(b.dueBy))

    return (
        <div className="space-y-3">
            <SectionHeading
                icon={ClipboardCheck}
                title={t("wirkung.queue")}
                count={rows.filter((row) => !row.confirmed).length}
            />
            <p className="text-xs text-muted-foreground">
                {t("wirkung.queueHint", { from, to })}
            </p>

            {rows.length === 0 ? (
                <p className="lp-card rounded-xl border bg-card p-4 text-xs text-muted-foreground">
                    {t("wirkung.queueEmpty")}
                </p>
            ) : (
                <ul className="space-y-2">
                    {rows.map((row) => (
                        <li
                            key={`${row.person.id}:${row.bausteinId}`}
                            className={cn(
                                "lp-card rounded-xl border bg-card p-3 transition-colors",
                                row.confirmed && "border-[var(--lp-good)]/40 bg-[var(--lp-good)]/[0.04]",
                            )}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium">{row.title}</p>
                                    <p className="mt-0.5 text-xs text-muted-foreground">
                                        {row.person.displayName} · {t("wirkung.completedOn")}{" "}
                                        {localizer.date(row.completedOn)}
                                    </p>
                                </div>
                                {row.confirmed && (
                                    <span
                                        className="flex shrink-0 items-center gap-1 text-xs font-medium"
                                        style={{ color: "var(--lp-good)" }}
                                    >
                                        <CheckCircle2 className="size-3.5" />
                                        {t("wirkung.confirmed")}
                                    </span>
                                )}
                            </div>

                            {!row.confirmed && (
                                <div className="mt-3 space-y-2">
                                    <p className="text-xs font-medium text-muted-foreground">
                                        {t("wirkung.drivers")}
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {REQUIRED_DRIVERS.map((driver) => (
                                            <DriverChip
                                                key={driver}
                                                label={t(`wirkung.driver.${driver}`)}
                                                onClick={() =>
                                                    confirmTransfer(row.person.id, row.bausteinId, [
                                                        driver,
                                                    ])
                                                }
                                            />
                                        ))}
                                    </div>
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        <Button
                                            size="sm"
                                            onClick={() =>
                                                confirmTransfer(row.person.id, row.bausteinId, [
                                                    "monitor",
                                                    "reinforce",
                                                ])
                                            }
                                        >
                                            {t("wirkung.applied.yes")}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() =>
                                                confirmTransfer(row.person.id, row.bausteinId, [])
                                            }
                                        >
                                            {t("wirkung.applied.no")}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

function DriverChip({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="rounded-full border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
        >
            {label}
        </button>
    )
}
